import {
    alertingSettingsSchema,
    alertsPageSchema,
    apiErrorSchema,
    canaryRunResultSchema,
    checkJobEnvelopeSchema,
    checkRegistryPageSchema,
    checkRunEnvelopeSchema,
    checkPasswordLinkResponseSchema,
    checkRunsPageSchema,
    checkToggleResponseSchema,
    clientCheckSettingsSchema,
    clientDetailEnvelopeSchema,
    clientOnboardingResponseSchema,
    clientRosterPageSchema,
    connectionsResponseSchema,
    createdClientSchema,
    createdIdSchema,
    createUserResponseSchema,
    ERROR_CODES,
    formRowsPageSchema,
    mailEventsPageSchema,
    muteRulesPageSchema,
    okResponseSchema,
    optionsResponseSchema,
    overviewResponseSchema,
    passwordLinkResponseSchema,
    providerAccountEnvelopeSchema,
    providerAccountsPageSchema,
    providerSecretUsageResponseSchema,
    provisionWebhooksResultSchema,
    qcMailboxStatusSchema,
    queuedResponseSchema,
    REQUEST_ID_HEADER,
    sessionUserEnvelopeSchema,
    syncFormsResultSchema,
    userEnvelopeSchema,
    usersPageSchema,
} from '@fkwd-qc/shared';
import type {
    AlertingSettings,
    AlertQuery,
    AlertsPage,
    CanaryRunResult,
    CheckJob,
    CheckRegistryPage,
    CheckPasswordLinkRequest,
    CheckPasswordLinkResponse,
    CheckRegistryQuery,
    CheckRun,
    CheckRunsPage,
    CheckToggleResponse,
    ClientCheckSettings,
    ClientDetailEnvelope,
    ClientOnboardingResponse,
    ClientRosterPage,
    ClientRosterQuery,
    OptionsQuery,
    OptionsResponse,
    ConnectionsResponse,
    CreateClientRequest,
    CreatedClient,
    CreatedId,
    CreateFormRequest,
    CreateProviderAccountRequest,
    CreateUserRequest,
    CreateUserResponse,
    ErrorCode,
    FormQuery,
    FormRowsPage,
    LoginRequest,
    MailEventQuery,
    MailEventsPage,
    MuteRulesPage,
    OkResponse,
    OverviewResponse,
    PasswordLinkResponse,
    ProviderAccountEnvelope,
    ProviderAccountsPage,
    ProviderSecretUsageResponse,
    ProvisionWebhooksResult,
    QcMailboxStatus,
    QueuedResponse,
    SessionUser,
    SetPasswordRequest,
    SyncFormsResult,
    UpdateClientRequest,
    UpdateFormRequest,
    UpdateProviderAccountRequest,
    UpdateUserRequest,
    UserEnvelope,
    UserQuery,
    UsersPage,
} from '@fkwd-qc/shared';
import type { z } from 'zod';

export type { ClientDetail, ClientRosterRow, PaginatedResponse } from '@fkwd-qc/shared';

/**
 * Every request is same-origin. In development Vite proxies this prefix to the worker.
 *
 * @since 1.0.0
 */
const API_BASE = '/api';

const HTTP_UNAUTHORIZED = 401;
const HTTP_NO_CONTENT = 204;
const HTTP_SERVER_ERROR = 500;

/**
 * The reference code assumed for a refused response that carried no readable error envelope.
 *
 * @since 1.3.0
 */
const FALLBACK_CODES: Readonly<Record<number, ErrorCode>> = {
    401: ERROR_CODES.unauthorized,
    403: ERROR_CODES.forbidden,
    404: ERROR_CODES.notFound,
    409: ERROR_CODES.conflict,
    410: ERROR_CODES.gone,
    429: ERROR_CODES.rateLimited,
};

/**
 * What the user reads when a successful response does not match the shape this build expects.
 *
 * @since 1.3.0
 */
export const SCHEMA_MISMATCH_MESSAGE = 'The server sent something this version of the dashboard cannot read.';

/**
 * Request options accepted by `apiFetch`. Headers are narrowed to a plain record so
 * callers can merge them without reaching for the `Headers` class.
 *
 * @since 1.0.0
 */
export interface ApiRequestInit extends Omit<RequestInit, 'headers'> {
    headers?: Record<string, string>;
}

type QueryValue = string | number | boolean | null | undefined;

type UnauthorizedHandler = () => void;

const unauthorizedHandlers = new Set<UnauthorizedHandler>();

/**
 * A failed API call, carrying the reference code and request id the worker returned so a
 * user can quote them in a bug report.
 *
 * @since 1.0.0
 */
export class ApiRequestError extends Error {
    /** @type {ErrorCode} */
    readonly code: ErrorCode;

    /** @type {string} */
    readonly requestId: string;

    /** @type {number} */
    readonly status: number;

    /** @type {unknown} */
    readonly details: unknown;

    /**
     * Builds a typed API error.
     *
     * @since 1.0.0
     * @param status - HTTP status of the failed response
     * @param code - reference code from the error envelope
     * @param message - human readable message from the error envelope
     * @param requestId - worker request identifier
     * @param details - optional structured detail payload
     */
    constructor(status: number, code: ErrorCode, message: string, requestId: string, details: unknown = null) {
        super(message);
        this.name = 'ApiRequestError';
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.details = details;
    }
}

/**
 * Registers a listener fired whenever any request comes back 401, so the session layer can
 * drop the cached user without every caller having to special-case auth.
 *
 * @since 1.0.0
 * @param handler - callback invoked on an unauthorized response
 * @returns an unsubscribe function
 */
export function onUnauthorized(handler: UnauthorizedHandler): () => void {
    unauthorizedHandlers.add(handler);

    return () => {
        unauthorizedHandlers.delete(handler);
    };
}

/**
 * Serialises defined query parameters into a search string, dropping empty values.
 *
 * @since 1.0.0
 * @param params - parameter map to serialise
 * @returns a leading-question-mark search string, or an empty string
 */
function toQueryString(params: Record<string, QueryValue>): string {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') {
            continue;
        }

        search.set(key, String(value));
    }

    const query = search.toString();

    return query === '' ? '' : `?${query}`;
}

/**
 * Words a refusal the worker did not explain, naming the reference code to quote.
 *
 * @since 1.3.0
 * @param code - the reference code assumed for the response
 * @returns the message to show
 */
function refusedMessage(code: ErrorCode): string {
    return `The request was refused. Try again, and quote reference ${code} if it keeps happening.`;
}

/**
 * Turns a failed response into a typed error, preferring the shared error envelope.
 *
 * @since 1.0.0
 * @param response - the failed response
 * @returns the error to throw
 */
async function toRequestError(response: Response): Promise<ApiRequestError> {
    const fallbackCode =
        FALLBACK_CODES[response.status] ??
        (response.status >= HTTP_SERVER_ERROR ? ERROR_CODES.internal : ERROR_CODES.validation);
    const requestId = response.headers.get(REQUEST_ID_HEADER) ?? '';

    try {
        const payload: unknown = await response.json();
        const parsed = apiErrorSchema.safeParse(payload);

        if (parsed.success) {
            return new ApiRequestError(
                response.status,
                parsed.data.code,
                parsed.data.message,
                parsed.data.requestId,
                parsed.data.details ?? null,
            );
        }
    } catch {
        // body was not json, fall through to the generic error below
    }

    return new ApiRequestError(response.status, fallbackCode, refusedMessage(fallbackCode), requestId);
}

/**
 * The only place this app calls `fetch`. Sends and receives JSON, forwards the session
 * cookie, validates the body against the schema and normalises failures onto ApiRequestError.
 *
 * @since 1.0.0
 * @param path - path below the API base, starting with a slash
 * @param schema - the shape the response body must have
 * @param init - optional request options
 * @returns the validated response body
 */
export async function apiFetch<TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    init: ApiRequestInit = {},
): Promise<z.output<TSchema>> {
    const response = await fetch(`${API_BASE}${path}`, {
        credentials: 'same-origin',
        ...init,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...init.headers,
        },
    });

    if (response.status === HTTP_UNAUTHORIZED) {
        for (const handler of unauthorizedHandlers) {
            handler();
        }
    }

    if (!response.ok) {
        throw await toRequestError(response);
    }

    const payload: unknown = response.status === HTTP_NO_CONTENT ? undefined : await response.json();
    const parsed = schema.safeParse(payload);

    if (!parsed.success) {
        throw new ApiRequestError(
            response.status,
            ERROR_CODES.internal,
            SCHEMA_MISMATCH_MESSAGE,
            response.headers.get(REQUEST_ID_HEADER) ?? '',
            parsed.error.issues,
        );
    }

    return parsed.data;
}

/**
 * Signs a user in.
 *
 * @since 1.0.0
 * @param body - email and password
 * @returns the signed-in user
 */
export async function login(body: LoginRequest): Promise<SessionUser> {
    const response = await apiFetch('/auth/login', sessionUserEnvelopeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    return response.user;
}

/**
 * Ends the current session.
 *
 * @since 1.0.0
 * @returns the acknowledgement
 */
export function logout(): Promise<OkResponse> {
    return apiFetch('/auth/logout', okResponseSchema, { method: 'POST' });
}

/**
 * Asks whose password a setup link would set, before any password is typed.
 *
 * @since 1.4.0
 * @param body - the link token
 * @returns the account the link belongs to
 */
export function checkPasswordLink(body: CheckPasswordLinkRequest): Promise<CheckPasswordLinkResponse> {
    return apiFetch('/auth/password-link/check', checkPasswordLinkResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * Sets a password from a setup link, which also signs the holder in.
 *
 * @since 1.4.0
 * @param body - the link token and the new password
 * @returns the signed-in user
 */
export async function setPassword(body: SetPasswordRequest): Promise<SessionUser> {
    const response = await apiFetch('/auth/set-password', sessionUserEnvelopeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    return response.user;
}

/**
 * Reads the signed-in user. Throws an ApiRequestError with status 401 when there is none.
 *
 * @since 1.0.0
 * @returns the signed-in user
 */
export async function getSession(): Promise<SessionUser> {
    const response = await apiFetch('/auth/session', sessionUserEnvelopeSchema);

    return response.user;
}

/**
 * Reads the whole overview payload: headline numbers, live check columns and per-client rows.
 *
 * @since 1.0.0
 * @returns the overview response
 */
export function getOverview(): Promise<OverviewResponse> {
    return apiFetch('/overview', overviewResponseSchema);
}

/**
 * Lists alerts, open by default.
 *
 * @since 1.0.0
 * @param query - optional state, client, severity and pagination filters
 * @returns a page of alerts with the resolved window it covers
 */
export function getAlerts(query: Partial<AlertQuery> = {}): Promise<AlertsPage> {
    const search = toQueryString({
        state: query.state,
        client: query.client,
        severity: query.severity,
        sort: query.sort,
        dir: query.dir,
        limit: query.limit,
        cursor: query.cursor,
    });

    return apiFetch(`/alerts${search}`, alertsPageSchema);
}

/**
 * Marks one alert resolved by the signed-in user.
 *
 * @since 1.0.0
 * @param id - alert identifier
 * @returns the worker's acknowledgement
 */
export function resolveAlert(id: number): Promise<OkResponse> {
    return apiFetch(`/alerts/${id}/resolve`, okResponseSchema, { method: 'POST' });
}

/**
 * Lists the rules that keep matching alerts out of Slack.
 *
 * @since 1.0.0
 * @returns a page of mute rules
 */
export function getMuteRules(): Promise<MuteRulesPage> {
    return apiFetch('/alerts/mute-rules', muteRulesPageSchema);
}

/**
 * Removes one mute rule, so alerts it was suppressing are delivered again.
 *
 * @since 1.0.0
 * @param id - mute rule identifier
 * @returns the acknowledgement
 */
export function deleteMuteRule(id: number): Promise<OkResponse> {
    return apiFetch(`/alerts/mute-rules/${id}`, okResponseSchema, { method: 'DELETE' });
}

/**
 * Lists mail events, newest first.
 *
 * @since 1.0.0
 * @param query - optional filter, client, search, date range and pagination filters
 * @returns a page of mail events
 */
export function getMailEvents(query: Partial<MailEventQuery> = {}): Promise<MailEventsPage> {
    const search = toQueryString({
        filter: query.filter,
        client: query.client,
        q: query.q,
        from: query.from,
        to: query.to,
        sort: query.sort,
        dir: query.dir,
        limit: query.limit,
        cursor: query.cursor,
    });

    return apiFetch(`/mail-events${search}`, mailEventsPageSchema);
}

/**
 * Builds the absolute path of the mail log CSV export for a set of filters.
 *
 * The export is a file download rather than JSON, so it is fetched by navigating to it and
 * letting the browser handle the attachment. Only the URL is built here.
 *
 * @since 1.0.0
 * @param query - the same filters the mail event list accepts
 * @returns the path to navigate to
 */
export function mailEventsExportUrl(query: Partial<MailEventQuery> = {}): string {
    const search = toQueryString({
        filter: query.filter,
        client: query.client,
        q: query.q,
        from: query.from,
        to: query.to,
        sort: query.sort,
        dir: query.dir,
    });

    return `${API_BASE}/mail-events/export.csv${search}`;
}

/**
 * Starts a mail log CSV download for a set of filters.
 *
 * @since 1.0.0
 * @param query - the same filters the mail event list accepts
 * @returns void
 */
export function downloadMailEventsCsv(query: Partial<MailEventQuery> = {}): void {
    window.location.assign(mailEventsExportUrl(query));
}

/**
 * Lists monitored forms with the outcome of their most recent canary.
 *
 * @since 1.0.0
 * @param query - optional client, sort and pagination filters
 * @returns a page of form rows
 */
export function getForms(query: Partial<FormQuery> = {}): Promise<FormRowsPage> {
    const search = toQueryString({
        client: query.client,
        sort: query.sort,
        dir: query.dir,
        limit: query.limit,
        cursor: query.cursor,
    });

    return apiFetch(`/forms${search}`, formRowsPageSchema);
}

/**
 * Registers a form for monitoring.
 *
 * @since 1.0.0
 * @param body - the client, identity, page path, selectors and thresholds
 * @returns the new form's id
 */
export function createForm(body: CreateFormRequest): Promise<CreatedId> {
    return apiFetch('/forms', createdIdSchema, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Updates one form registration, for example to switch its canary on or off.
 *
 * @since 1.0.0
 * @param id - form identifier
 * @param body - fields to change
 * @returns the acknowledgement
 */
export function updateForm(id: number, body: UpdateFormRequest): Promise<OkResponse> {
    return apiFetch(`/forms/${id}`, okResponseSchema, { method: 'PATCH', body: JSON.stringify(body) });
}

/**
 * Removes one form registration and everything scheduled against it.
 *
 * @since 1.0.0
 * @param id - form identifier
 * @returns the acknowledgement
 */
export function deleteForm(id: number): Promise<OkResponse> {
    return apiFetch(`/forms/${id}`, okResponseSchema, { method: 'DELETE' });
}

/**
 * Queues, and where a runner exists starts, a canary run for one form.
 *
 * @since 1.0.0
 * @param formId - form identifier
 * @returns what was queued and what started
 */
export function runCanary(formId: number): Promise<CanaryRunResult> {
    return apiFetch(`/forms/${formId}/run-canary`, canaryRunResultSchema, { method: 'POST' });
}

/**
 * Queues, and where a runner exists starts, a canary run for one form whose notification goes to the client
 * with the QC mailbox copied.
 *
 * @since 1.4.0
 * @param formId - form identifier
 * @returns what was queued and what started
 */
export function sendCanaryToClient(formId: number): Promise<CanaryRunResult> {
    return apiFetch(`/forms/${formId}/send-to-client`, canaryRunResultSchema, { method: 'POST' });
}

/**
 * Queues a canary run for every enabled form.
 *
 * @since 1.0.0
 * @param clientSlug - narrows the run to one client, or every client when omitted
 * @returns how many runs were queued and how many started
 */
export function runAllCanaries(clientSlug?: string): Promise<CanaryRunResult> {
    return apiFetch('/canaries/run-all', canaryRunResultSchema, {
        method: 'POST',
        body: JSON.stringify(clientSlug ? { clientSlug } : {}),
    });
}

/**
 * Reads whether canary mail is confirmed to reach the QC mailbox.
 *
 * @since 1.0.0
 * @returns the mailbox status
 */
export function getQcMailbox(): Promise<QcMailboxStatus> {
    return apiFetch('/settings/qc-mailbox', qcMailboxStatusSchema);
}

/**
 * Confirms, or withdraws confirmation of, the QC mailbox.
 *
 * @since 1.0.0
 * @param isVerified - whether canary mail was seen arriving
 * @returns the stored mailbox status
 */
export function setQcMailbox(isVerified: boolean): Promise<QcMailboxStatus> {
    return apiFetch('/settings/qc-mailbox', qcMailboxStatusSchema, {
        method: 'PUT',
        body: JSON.stringify({ isVerified }),
    });
}

/**
 * Reads the check registry: every check the code declares, plus whether it is switched on.
 *
 * @since 1.0.0
 * @param query - the optional sort order
 * @returns a page of registry rows
 */
export function getChecks(query: Partial<CheckRegistryQuery> = {}): Promise<CheckRegistryPage> {
    return apiFetch(`/checks${toQueryString({ sort: query.sort, dir: query.dir })}`, checkRegistryPageSchema);
}

/**
 * Switches one check on or off.
 *
 * @since 1.0.0
 * @param slug - check slug
 * @param isEnabled - the requested state
 * @returns the check and the state it now has
 */
export function toggleCheck(slug: string, isEnabled: boolean): Promise<CheckToggleResponse> {
    return apiFetch(`/checks/${slug}`, checkToggleResponseSchema, {
        method: 'PATCH',
        body: JSON.stringify({ isEnabled }),
    });
}

/**
 * Queues every switched-on check against every client instead of waiting for the schedules.
 *
 * @since 1.0.0
 * @returns confirmation that the sweep was queued
 */
export function runAllChecks(): Promise<QueuedResponse> {
    return apiFetch('/checks/run-all', queuedResponseSchema, { method: 'POST' });
}

/**
 * Queues one check against every client, or returns the run already in progress for it.
 *
 * @since 1.5.0
 * @param slug - check slug
 * @returns the queued or running job
 */
export async function runCheckForAllClients(slug: string): Promise<CheckJob> {
    const response = await apiFetch(`/checks/${slug}/run-all`, checkJobEnvelopeSchema, { method: 'POST' });

    return response.job;
}

/**
 * Runs one check for one client now and returns the run it recorded. The request stays open
 * for as long as the check takes.
 *
 * @since 1.5.0
 * @param clientSlug - client slug
 * @param checkSlug - check slug
 * @returns the recorded run
 */
export async function runClientCheck(clientSlug: string, checkSlug: string): Promise<CheckRun> {
    const response = await apiFetch(`/clients/${clientSlug}/checks/${checkSlug}/run`, checkRunEnvelopeSchema, {
        method: 'POST',
    });

    return response.run;
}

/**
 * Reads how one check is set up for one client.
 *
 * @since 1.5.0
 * @param clientSlug - client slug
 * @param checkSlug - check slug
 * @returns the switch and the overrides in effect
 */
export function getClientCheckSettings(clientSlug: string, checkSlug: string): Promise<ClientCheckSettings> {
    return apiFetch(`/clients/${clientSlug}/checks/${checkSlug}/settings`, clientCheckSettingsSchema);
}

/**
 * Stores how one check is set up for one client.
 *
 * @since 1.5.0
 * @param clientSlug - client slug
 * @param checkSlug - check slug
 * @param body - the switch and the overrides to store
 * @returns the settings as stored
 */
export function updateClientCheckSettings(
    clientSlug: string,
    checkSlug: string,
    body: ClientCheckSettings,
): Promise<ClientCheckSettings> {
    return apiFetch(`/clients/${clientSlug}/checks/${checkSlug}/settings`, clientCheckSettingsSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
}

/**
 * Lists the client roster.
 *
 * @since 1.0.0
 * @param query - optional sort and pagination filters
 * @returns a page of clients
 */
export function getClients(query: Partial<ClientRosterQuery> = {}): Promise<ClientRosterPage> {
    const search = toQueryString({
        sort: query.sort,
        dir: query.dir,
        limit: query.limit,
        cursor: query.cursor,
    });

    return apiFetch(`/clients${search}`, clientRosterPageSchema);
}

/**
 * Lists every client a picker may offer, as slug and name only.
 *
 * @since 1.4.0
 * @param query - an optional search term and cap
 * @returns the client choices, and whether the cap left any behind
 */
export function getClientOptions(query: Partial<OptionsQuery> = {}): Promise<OptionsResponse> {
    return apiFetch(`/clients/options${toQueryString({ q: query.q, limit: query.limit })}`, optionsResponseSchema);
}

/**
 * Reads one client's configuration. The site key is never returned; `hasSiteKey` reports
 * only whether one is stored.
 *
 * @since 1.0.0
 * @param slug - client slug
 * @returns the wrapped client record
 */
export function getClient(slug: string): Promise<ClientDetailEnvelope> {
    return apiFetch(`/clients/${slug}`, clientDetailEnvelopeSchema);
}

/**
 * Adds a client to the roster.
 *
 * @since 1.0.0
 * @param body - the new client's details
 * @returns the new client's id and slug
 */
export function createClient(body: CreateClientRequest): Promise<CreatedClient> {
    return apiFetch('/clients', createdClientSchema, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Updates one client's configuration.
 *
 * @since 1.0.0
 * @param slug - client slug
 * @param body - fields to change
 * @returns the acknowledgement
 */
export function updateClient(slug: string, body: UpdateClientRequest): Promise<OkResponse> {
    return apiFetch(`/clients/${slug}`, okResponseSchema, { method: 'PATCH', body: JSON.stringify(body) });
}

/**
 * Points a client's provider webhooks at this dashboard again.
 *
 * @since 1.0.0
 * @param slug - the client to provision
 * @returns what the provider reported
 */
export function provisionClientWebhooks(slug: string): Promise<ProvisionWebhooksResult> {
    return apiFetch(`/clients/${slug}/provision-webhooks`, provisionWebhooksResultSchema, { method: 'POST' });
}

/**
 * Pulls a client's forms from its site and registers them. Upstream trouble comes back
 * as a request error whose message says what to check on the site.
 *
 * @since 1.0.0
 * @param slug - client slug
 * @returns what the sync created, updated and skipped
 */
export function syncClientForms(slug: string): Promise<SyncFormsResult> {
    return apiFetch(`/clients/${slug}/sync-forms`, syncFormsResultSchema, { method: 'POST' });
}

/**
 * Reads the four onboarding steps for one client with their completion state.
 *
 * @since 1.0.0
 * @param slug - client slug
 * @returns the onboarding steps and the state they add up to
 */
export function getClientOnboarding(slug: string): Promise<ClientOnboardingResponse> {
    return apiFetch(`/clients/${slug}/onboarding`, clientOnboardingResponseSchema);
}

/**
 * Reads recent runs of one check for one client, newest first.
 *
 * @since 1.0.0
 * @param clientSlug - client slug
 * @param checkSlug - check slug
 * @param limit - how many runs to return
 * @returns a page of check runs
 */
export function getCheckRuns(clientSlug: string, checkSlug: string, limit: number): Promise<CheckRunsPage> {
    const search = toQueryString({ limit });

    return apiFetch(`/clients/${clientSlug}/checks/${checkSlug}/runs${search}`, checkRunsPageSchema);
}

/**
 * Reads one check run by its qc_run_id.
 *
 * @since 1.0.0
 * @param qcRunId - run identifier
 * @returns the check run
 */
export async function getRun(qcRunId: string): Promise<CheckRun> {
    const response = await apiFetch(`/checks/runs/${qcRunId}`, checkRunEnvelopeSchema);

    return response.run;
}

/**
 * Reads the live health of every external connection.
 *
 * @since 1.0.0
 * @returns the connections response
 */
export function getConnections(): Promise<ConnectionsResponse> {
    return apiFetch('/settings/connections', connectionsResponseSchema);
}

/**
 * Reads the alert routing table and storm thresholds.
 *
 * @since 1.0.0
 * @returns the alerting settings
 */
export function getAlerting(): Promise<AlertingSettings> {
    return apiFetch('/settings/alerting', alertingSettingsSchema);
}

/**
 * Lists the configured provider accounts. Each row names the Worker secret holding its
 * credentials rather than carrying the credentials themselves.
 *
 * @since 1.0.0
 * @returns a page of provider accounts
 */
export function getProviderAccounts(): Promise<ProviderAccountsPage> {
    return apiFetch('/provider-accounts', providerAccountsPageSchema);
}

/**
 * Reads how many accounts share each Worker secret name, counted on the server.
 *
 * @since 1.4.0
 * @returns one row per provider and secret name
 */
export function getProviderSecretUsage(): Promise<ProviderSecretUsageResponse> {
    return apiFetch('/provider-accounts/secrets', providerSecretUsageResponseSchema);
}

/**
 * Adds a provider account.
 *
 * @since 1.0.0
 * @param body - the provider, label, secret name and optional API base
 * @returns the wrapped created account
 */
export function createProviderAccount(body: CreateProviderAccountRequest): Promise<ProviderAccountEnvelope> {
    return apiFetch('/provider-accounts', providerAccountEnvelopeSchema, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * Corrects an existing provider account.
 *
 * @since 1.0.0
 * @param id - the account to change
 * @param body - the fields to change
 * @returns the wrapped updated account
 */
export function updateProviderAccount(
    id: number,
    body: UpdateProviderAccountRequest,
): Promise<ProviderAccountEnvelope> {
    return apiFetch(`/provider-accounts/${id}`, providerAccountEnvelopeSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

/**
 * Removes a provider account no client is using.
 *
 * @since 1.0.0
 * @param id - the account to remove
 * @returns the acknowledgement
 */
export function deleteProviderAccount(id: number): Promise<OkResponse> {
    return apiFetch(`/provider-accounts/${id}`, okResponseSchema, { method: 'DELETE' });
}

/**
 * Lists dashboard users.
 *
 * @since 1.0.0
 * @param query - optional sort and pagination filters
 * @returns a page of users
 */
export function getUsers(query: Partial<UserQuery> = {}): Promise<UsersPage> {
    const search = toQueryString({
        sort: query.sort,
        dir: query.dir,
        limit: query.limit,
        cursor: query.cursor,
    });

    return apiFetch(`/users${search}`, usersPageSchema);
}

/**
 * Invites a dashboard user.
 *
 * @since 1.0.0
 * @param body - the new user's details
 * @returns the invited user and the link that lets them set a password
 */
export function createUser(body: CreateUserRequest): Promise<CreateUserResponse> {
    return apiFetch('/users', createUserResponseSchema, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Issues a fresh password setup link for a user, retiring any unused one.
 *
 * @since 1.4.0
 * @param id - user identifier
 * @returns the link and when it expires
 */
export function createPasswordLink(id: number): Promise<PasswordLinkResponse> {
    return apiFetch(`/users/${id}/password-link`, passwordLinkResponseSchema, { method: 'POST' });
}

/**
 * Updates a dashboard user.
 *
 * @since 1.0.0
 * @param id - user identifier
 * @param body - fields to change
 * @returns the wrapped updated user
 */
export function updateUser(id: number, body: UpdateUserRequest): Promise<UserEnvelope> {
    return apiFetch(`/users/${id}`, userEnvelopeSchema, { method: 'PATCH', body: JSON.stringify(body) });
}
